import {
  type Collaborator,
  type InviteToken,
  SharingError,
  useShareManager,
} from '@jbroll/rowboat-sharing-react';
import { useState } from 'react';
import { Alert, Button, Drawer, Field, Muted, TextInput } from './ui';

const ROLE_NAMES: Record<string, string> = { reader: 'Viewer', writer: 'Editor', admin: 'Admin' };
// No Admin: an invited admin can link the project under their own root, which the owner
// cannot undo (rowboat R393). The backend guard refuses it too.
const INVITE_ROLES = ['reader', 'writer'];
const EXPIRY_DAYS = [1, 7, 14, 30];
// The server's limit on an invite's target name.
const MAX_NAME = 200;

const MESSAGES: Record<string, string> = {
  root_group: "This project is in your account's own group, so it can't be shared.",
  forbidden: 'Only an admin of this project can manage sharing.',
  invalid_target_name: "The project's name can't be used in an invite. Rename the project.",
  email_required: 'Enter an email address.',
  invalid_role: 'Pick Viewer or Editor.',
  invalid_expiry: 'Pick an expiry of 1 to 30 days.',
  invite_not_found: 'That invite was already used or revoked.',
  cannot_remove_self: "You can't remove yourself.",
  last_admin: "The project must keep at least one admin, so this person can't be removed.",
};

function errorMessage(err: Error): string {
  if (!(err instanceof SharingError)) {
    return 'Could not reach the server. Check the connection and try again.';
  }
  if (err.code && MESSAGES[err.code]) return MESSAGES[err.code];
  if (err.status === 401) return 'Sign in again to manage sharing.';
  // No code: the answer came from a proxy or the server's error handling, not a share route,
  // and its message is a request line, not a sentence for people.
  if (err.code === null)
    return `The server could not complete the request (${err.status}). Try again.`;
  return err.message;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const selectClass =
  'min-h-11 w-full rounded-xl border border-divider bg-surface-primary px-3 py-2.5 text-base text-content-primary';
const rowButton = 'shrink-0 px-3 text-sm';

// A deleted account still holds its membership until an admin removes it.
const personName = (c: Collaborator) => (c.deleted ? `Deleted account (${c.accountId})` : c.email);
export const roleName = (role: string) => ROLE_NAMES[role] ?? role;

export function ShareDrawer({
  groupId,
  projectName,
  selfId,
  onClose,
}: {
  groupId: string;
  projectName: string;
  selfId: string;
  onClose: () => void;
}) {
  const share = useShareManager(groupId);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('reader');
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const to = email.trim();
  const ready = /^\S+@\S+$/.test(to) && !busy;

  const invite = async (sendEmail: boolean) => {
    setBusy(true);
    setNotice('');
    const result = await share.invite(to, role, {
      sendEmail,
      expiresInDays: days,
      targetName: projectName.trim().slice(0, MAX_NAME) || undefined,
    });
    setBusy(false);
    if (!result) return;
    setEmail('');
    if (sendEmail) {
      setNotice(
        result.emailSent
          ? `Invite emailed to ${to}.`
          : `The email could not be sent. Copy the link below and send it to ${to}.`,
      );
    } else {
      setNotice(
        (await copyText(result.shareUrl))
          ? `Link copied. Send it to ${to}; it works only for that address.`
          : `Copy the link below and send it to ${to}.`,
      );
    }
  };

  const remove = (c: Collaborator) => {
    if (!window.confirm(`Remove ${personName(c)} from ${projectName}?`)) return;
    setNotice('');
    void share.remove(c.accountId);
  };

  const revoke = (i: InviteToken) => {
    if (!window.confirm(`Revoke the invite to ${i.recipientEmail}?`)) return;
    setNotice('');
    void share.revoke(i.token);
  };

  return (
    <Drawer title={`Share ${projectName}`} onClose={onClose}>
      <Field label="Email">
        <TextInput
          type="email"
          aria-label="Email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
        />
      </Field>
      <Field label="Role">
        <select
          aria-label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className={selectClass}
        >
          {INVITE_ROLES.map((value) => (
            <option key={value} value={value}>
              {roleName(value)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Link expires after">
        <select
          aria-label="Link expires after"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className={selectClass}
        >
          {EXPIRY_DAYS.map((d) => (
            <option key={d} value={d}>
              {d === 1 ? '1 day' : `${d} days`}
            </option>
          ))}
        </select>
      </Field>
      <div className="flex gap-2 pt-1">
        <Button
          variant="secondary"
          className="flex-1 px-3"
          disabled={!ready}
          onClick={() => void invite(false)}
        >
          Copy link
        </Button>
        <Button className="flex-1 px-3" disabled={!ready} onClick={() => void invite(true)}>
          Email invite
        </Button>
      </div>
      {notice && <output className="block text-sm text-content-secondary">{notice}</output>}
      {share.lastShareUrl && (
        <Field label="Share link">
          <TextInput
            readOnly
            aria-label="Share link"
            value={share.lastShareUrl}
            onFocus={(e) => e.target.select()}
          />
        </Field>
      )}
      {share.error && <Alert>{errorMessage(share.error)}</Alert>}

      <section aria-label="People with access" className="pt-2">
        <h3 className="text-sm font-semibold text-content-primary">People with access</h3>
        {share.loading && share.collaborators.length === 0 && <Muted>Loading…</Muted>}
        <ul className="divide-y divide-divider">
          {share.collaborators.map((c) => (
            <li key={c.accountId} className="flex min-h-11 items-center gap-2 py-1 text-sm">
              <span className="min-w-0 flex-1 truncate text-content-primary">
                {personName(c)}
                {c.accountId === selfId && ' (you)'}
              </span>
              <span className="text-content-tertiary">{roleName(c.role)}</span>
              {c.accountId !== selfId && (
                <Button
                  variant="secondary"
                  className={rowButton}
                  aria-label={`Remove ${personName(c)}`}
                  onClick={() => remove(c)}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>

      {share.pendingInvites.length > 0 && (
        <section aria-label="Pending invites" className="pt-2">
          <h3 className="text-sm font-semibold text-content-primary">Pending invites</h3>
          <ul className="divide-y divide-divider">
            {share.pendingInvites.map((i) => (
              <li key={i.token} className="flex min-h-11 items-center gap-2 py-1 text-sm">
                <span className="min-w-0 flex-1 text-content-primary">
                  <span className="block truncate">{i.recipientEmail}</span>
                  <span className="text-content-tertiary">
                    {roleName(i.role)} · expires {new Date(i.expiresAt * 1000).toLocaleDateString()}
                  </span>
                </span>
                <Button
                  variant="secondary"
                  className={rowButton}
                  aria-label={`Revoke invite to ${i.recipientEmail}`}
                  onClick={() => revoke(i)}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Drawer>
  );
}
